import { useNavigate } from "@tanstack/react-router"
import type { ExpandedState, OnChangeFn, Row, SortingState } from "@tanstack/react-table"
import type { FC, RefObject } from "react"

import { useProductCostBreakdownColumns } from "../hooks/useProductCostBreakdownColumns"
import type { ProductCostBreakdownRow } from "../types"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { ProductCostBreakdownDetail } from "@/screens/Procurement/CostBreakdown/components/ProductCostBreakdownDetail"

interface ProductCostBreakdownsTableProps {
  data: ProductCostBreakdownRow[]
  isLoading: boolean
  expandedState: ExpandedState
  sortingState: SortingState
  tableContainerRef: RefObject<HTMLDivElement>
  onExpandedChange: OnChangeFn<ExpandedState>
  onSortingChange: OnChangeFn<SortingState>
  onScroll: () => void
}

export const ProductCostBreakdownsTable: FC<ProductCostBreakdownsTableProps> = ({
  data,
  isLoading,
  expandedState,
  sortingState,
  tableContainerRef,
  onExpandedChange,
  onSortingChange,
  onScroll,
}) => {
  const navigate = useNavigate()
  const columns = useProductCostBreakdownColumns()

  const handleRowClick = (row: Row<ProductCostBreakdownRow>) => {
    navigate({
      to: "/app/procurement/cost-breakdown",
      search: {
        query: row.original.goodsReceipt.agrNumber ?? "",
        isProductCostCompleted: [true, false],
      },
    })
  }

  const renderExpandedContent = (row: Row<ProductCostBreakdownRow>) => {
    const { costBreakdown } = row.original.purchaseOrderLine

    if (!costBreakdown) {
      return null
    }

    return <ProductCostBreakdownDetail lineCostBreakdown={costBreakdown} />
  }

  return (
    <AerosBaseTable
      features={{
        table: { stickyHeader: true },
        tanstackOptions: {
          data,
          columns,
          state: { sorting: sortingState, expanded: expandedState, isLoading },
          manualSorting: true,
          enableSortingRemoval: false,
          enableMultiSort: false,
          getRowCanExpand: () => true,
          renderExpandedContent,
          onSortingChange,
          onExpandedChange,
        },
      }}
      slotProps={{
        container: { ref: tableContainerRef, onScroll },
        bodyRow: {
          className: "hover:bg-primary-100 cursor-pointer",
          onClick: (value) => handleRowClick(value as Row<ProductCostBreakdownRow>),
        },
        bodyCellExpanded: { className: "bg-primary-100" },
      }}
      noDataMessage='No product cost breakdowns found'
    />
  )
}
